import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ChangeEvent,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from './Input'
import {
  formatDateWords,
  localDateYearsAhead,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIMEZONE,
  DEFAULT_CALENDAR,
  type DateFormatPattern,
  type CalendarSystem,
} from '@/lib/date'

// EP-149 T149-090 (PAY-4/DAT-2): a shared bounded date input. Every date field
// in the app is currently a raw `<Input type="date">`, with no bounds, no
// Buddhist-year guard, and a browser-locale segment order that can silently
// disagree with the account's configured date_format. This component wraps
// the native input to fix all three, and is composed over `Input` so it
// reuses its label/error/hint slots rather than reinventing them.

const MIN_DATE = '2000-01-01'
const YEARS_AHEAD_ALLOWED = 2
// A year typed above this is unambiguously a Buddhist year mistaken for the
// box's plain YYYY digits (today's Buddhist year is ~2569; the max Gregorian
// bound this component ever allows is a few years past 2000+today, nowhere
// near 2400) rather than a genuine three-digit-off Gregorian typo.
const BUDDHIST_YEAR_THRESHOLD = 2400
const BUDDHIST_TO_GREGORIAN_OFFSET = 543

const ORDER_HINT_KEY: Record<DateFormatPattern, string> = {
  'DD/MM/YYYY': 'date_input.order_dd_mm_yyyy',
  'MM/DD/YYYY': 'date_input.order_mm_dd_yyyy',
  'YYYY-MM-DD': 'date_input.order_yyyy_mm_dd',
}

// `useLocalizationConfig().format` (and `AccountSettings.date_format`) are
// typed as plain `string` app-wide, even though the account can only ever
// hold one of the 3 literal patterns below (enforced by the Settings UI /
// server, not by TypeScript). Narrowing here, rather than typing `dateFormat`
// itself as the strict union, is what lets every caller pass that hook's
// return value straight through with no cast.
function resolveDateFormat(format: string): DateFormatPattern {
  return format === 'MM/DD/YYYY' || format === 'YYYY-MM-DD' ? format : DEFAULT_DATE_FORMAT
}

// Renders a plain YYYY-MM-DD calendar date (no time component, so no
// timezone conversion belongs here) in the account's layout, adding 543 to
// the year for a Buddhist-calendar account. Used only for echoing the min/max
// bounds in the error caption; NOT a general-purpose formatter (see
// `formatDate` in lib/date.ts for that, which is for real timestamps).
function formatPlainDate(iso: string, format: DateFormatPattern, calendar: CalendarSystem): string {
  const [y, m, d] = iso.split('-')
  const yyyy = calendar === 'buddhist' ? String(Number(y) + BUDDHIST_TO_GREGORIAN_OFFSET) : y
  switch (format) {
    case 'MM/DD/YYYY':
      return `${m}/${d}/${yyyy}`
    case 'YYYY-MM-DD':
      return `${yyyy}-${m}-${d}`
    case 'DD/MM/YYYY':
    default:
      return `${d}/${m}/${yyyy}`
  }
}

// The shape DateInput's own onChange always passes. It mimics a native
// ChangeEvent's `target.value`/`target.name` so it satisfies BOTH
// react-hook-form call conventions without a cast at the call site:
// `register()`'s returned onChange expects `{ target: any; type?: any }`,
// and Controller's `field.onChange` accepts anything at all
// (`(...event: any[]) => void`); both are exercised by this component's
// own tests. A caller not using RHF can destructure `event.target.value`
// exactly like a real change event.
export interface DateInputChangeEvent {
  target: { value: string; name?: string }
  type?: string
}

export interface DateInputProps {
  name?: string
  /** Controlled mode (e.g. Controller's `field.value`). Omit for register()/uncontrolled mode. */
  value?: string
  /** Uncontrolled mode's initial value. Ignored when `value` is provided. */
  defaultValue?: string
  onChange?: (event: DateInputChangeEvent) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  label?: string
  /** Highest-priority caption (e.g. RHF's "required" message). Suppresses the hint/words echo. */
  error?: string
  required?: boolean
  disabled?: boolean
  id?: string
  className?: string
  inputClassName?: string
  /**
   * Defaults to 2000-01-01. `string | number` (not just `string`) so
   * register()'s returned `min`/`max` (typed loosely for native HTML
   * min/maxLength validation attrs, which this component doesn't use) can
   * be spread straight through with no cast; a `number` is coerced to a
   * plain YYYY-MM-DD string.
   */
  min?: string | number
  /** Defaults to today + 2 years, computed in `timezone`. See `min`. */
  max?: string | number
  /**
   * Some server-accepted dates genuinely have no ceiling (e.g. a lease's
   * ended_at, EP-149 LSE-1): the default today+2-years max would silently
   * reject a value the API happily takes. Set `unboundedMax` to drop the
   * upper bound entirely: the native input renders no `max` attribute, no
   * date is ever out-of-range on the high side, and the bounds caption (once
   * the field goes out of range on the low side) reads min-only. Ignored
   * when an explicit `max` is also passed; `min`, the Buddhist-year
   * conversion, the order hint and the words echo are unaffected.
   */
  unboundedMax?: boolean
  /**
   * Account's configured layout. Defaults to DD/MM/YYYY. Typed as plain
   * `string` (not the stricter `DateFormatPattern`) so `useLocalizationConfig()
   * .format` passes straight through with no cast; an unrecognized value
   * falls back to DD/MM/YYYY.
   */
  dateFormat?: string
  /** IANA timezone, used only to resolve "today" for the default max bound. */
  timezone?: string
  /** 'gregorian' | 'buddhist'. Defaults to gregorian. */
  calendar?: CalendarSystem
  /** BCP-47 UI locale for the words echo (e.g. i18n.language). Defaults to 'en'. */
  locale?: string
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>((props, forwardedRef) => {
  const {
    name,
    value: controlledValue,
    defaultValue,
    onChange,
    onBlur,
    label,
    error: externalError,
    required,
    disabled,
    id,
    className,
    inputClassName,
    min,
    max,
    unboundedMax,
    dateFormat: dateFormatProp = DEFAULT_DATE_FORMAT,
    timezone = DEFAULT_TIMEZONE,
    calendar = DEFAULT_CALENDAR,
    locale = 'en',
  } = props
  const { t } = useTranslation('common')
  const dateFormat = resolveDateFormat(dateFormatProp)

  const isControlled = controlledValue !== undefined
  const minDate = min !== undefined ? String(min) : MIN_DATE
  // An explicit `max` always wins; otherwise `unboundedMax` drops the upper
  // bound entirely (undefined: no native `max` attribute, no high-side
  // bounds check, see EP-149 LSE-1); the default is today + 2 years.
  const maxDate = max !== undefined
    ? String(max)
    : unboundedMax
      ? undefined
      : localDateYearsAhead(YEARS_AHEAD_ALLOWED, timezone)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node
    if (typeof forwardedRef === 'function') forwardedRef(node)
    else if (forwardedRef) (forwardedRef as RefObject<HTMLInputElement | null>).current = node
  }

  // Mirrors the box's real displayed value regardless of controlled/
  // uncontrolled mode, used only to compute the caption (words echo), never
  // to decide what reaches the underlying native input in uncontrolled mode
  // (that stays genuinely uncontrolled so register()'s ref-applied default
  // value is never fought by a React re-render).
  const [shadowValue, setShadowValue] = useState(controlledValue ?? defaultValue ?? '')
  const [conversionNote, setConversionNote] = useState<string | null>(null)
  const [boundsInvalid, setBoundsInvalid] = useState(false)
  const [touched, setTouched] = useState(false)

  // The value the field (and the form behind it) actually holds: the last
  // one we forwarded via onChange, or an externally-driven value (e.g. a
  // Controller reset), or the initial value if neither ever happened. Blur
  // reverts an out-of-range box back to this, see handleBlur below (F1: a
  // box showing an out-of-range date and a form silently holding the old one
  // is worse than either alone, so the two must never disagree once the
  // field is left).
  const lastValidValueRef = useRef(controlledValue ?? defaultValue ?? '')

  useEffect(() => {
    if (controlledValue !== undefined && controlledValue !== shadowValue) {
      setShadowValue(controlledValue)
      lastValidValueRef.current = controlledValue
    }
    // shadowValue is intentionally excluded: this effect only pulls in an
    // externally-driven value change (e.g. form reset), never re-fires from
    // our own keystroke-driven updates to shadowValue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledValue])

  // register()-mode, no explicit `defaultValue`: RHF applies its own
  // `defaultValues` entry to the DOM node imperatively via the ref (the
  // README's documented register()-mode caveat), a write this render never
  // sees, so `lastValidValueRef`'s own initializer above landed on '' even
  // though the box shows a real starting value. Read it once, straight off
  // the DOM, after mount (React attaches refs, including RHF's, before any
  // passive effect runs) so a blur-revert has something correct to fall
  // back to even when the caller skipped the recommended `defaultValue`.
  useEffect(() => {
    if (!isControlled && defaultValue === undefined && inputRef.current) {
      lastValidValueRef.current = inputRef.current.value
    }
    // Mount-only: register()'s own imperative DOM write happens once, at
    // attach time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const emit = (isoValue: string) => {
    lastValidValueRef.current = isoValue
    onChange?.({ target: { value: isoValue, name }, type: 'change' })
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value // native type=date sanitizes to '' or a strict YYYY-MM-DD
    if (raw === '') {
      setConversionNote(null)
      setBoundsInvalid(false)
      setShadowValue('')
      emit('')
      return
    }

    const [yStr, mStr, dStr] = raw.split('-')
    const typedYear = Number(yStr)
    let correctedIso = raw
    let note: string | null = null
    if (typedYear > BUDDHIST_YEAR_THRESHOLD) {
      const convertedYear = typedYear - BUDDHIST_TO_GREGORIAN_OFFSET
      correctedIso = `${String(convertedYear).padStart(4, '0')}-${mStr}-${dStr}`
      note = t('date_input.buddhist_conversion', { from: typedYear, to: convertedYear })
      // Uncontrolled mode: the DOM node still shows the raw typed year until
      // we write the corrected value back into it ourselves (there is no
      // React `value=` prop fighting this write, so it sticks).
      if (!isControlled && inputRef.current) {
        inputRef.current.value = correctedIso
      }
    }
    setConversionNote(note)
    setShadowValue(correctedIso)

    const withinBounds = correctedIso >= minDate && (maxDate === undefined || correctedIso <= maxDate)
    setBoundsInvalid(!withinBounds)
    if (!withinBounds) return // never forwarded: out-of-range never reaches onChange
    emit(correctedIso)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setTouched(true)
    // F1: an out-of-range box left unresolved is reverted to the last value
    // the form actually holds, in the real DOM node, BEFORE onBlur is
    // forwarded. That order matters: register()'s own onBlur handler reads
    // `e.target.value` straight off the DOM (uncontrolled mode has no other
    // source of truth), so forwarding first would let the bad date reach
    // RHF's internal state via blur even though onChange never emitted it.
    // boundsInvalid itself is left set (not cleared here) so the error
    // caption stays up after the revert, per spec, until the next change.
    if (boundsInvalid) {
      const revertTo = lastValidValueRef.current
      if (inputRef.current) inputRef.current.value = revertTo
      setShadowValue(revertTo)
    }
    onBlur?.(e)
  }

  // Enter submits the surrounding form without the field ever being left,
  // so the blur-revert above never runs. With an out-of-range date still in
  // the box that would save the earlier date while the screen shows another
  // one. Block the implicit submit and show the range error instead. The
  // live DOM value is checked, not boundsInvalid, which stays set after a
  // blur-revert so the caption can stay up.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    const current = inputRef.current?.value ?? ''
    if (current === '') return
    const outOfRange = current < minDate || (maxDate !== undefined && current > maxDate)
    if (!outOfRange) return
    e.preventDefault()
    setTouched(true)
  }

  const words = (() => {
    if (!shadowValue) return ''
    const [y, m, d] = shadowValue.split('-').map(Number)
    if (!y || !m || !d) return ''
    // Local Date components (never parsed from the ISO string as UTC
    // midnight): this is a plain calendar date with no time component, so
    // constructing and formatting in the same local frame is the one
    // combination that can't drift a day in either direction.
    return formatDateWords(new Date(y, m - 1, d), { locale, calendar })
  })()

  const orderHint = t(ORDER_HINT_KEY[dateFormat])

  // Exactly one caption at a time: an external error (e.g. RHF "required")
  // always wins, then the Buddhist-year conversion note while it's live,
  // then the bounds error once the field has been left invalid, and only
  // otherwise the default order hint + words echo.
  let caption = orderHint + (words ? ` · ${words}` : '')
  let captionIsError = false
  if (externalError) {
    caption = externalError
    captionIsError = true
  } else if (conversionNote) {
    caption = conversionNote
  } else if (touched && boundsInvalid) {
    caption = maxDate === undefined
      ? t('date_input.out_of_range_min', { min: formatPlainDate(minDate, dateFormat, calendar) })
      : t('date_input.out_of_range', {
          min: formatPlainDate(minDate, dateFormat, calendar),
          max: formatPlainDate(maxDate, dateFormat, calendar),
        })
    captionIsError = true
  }

  return (
    <Input
      ref={setRefs}
      type="date"
      id={id}
      name={name}
      label={label}
      required={required}
      disabled={disabled}
      className={className}
      inputClassName={inputClassName}
      min={minDate}
      max={maxDate}
      value={isControlled ? shadowValue : undefined}
      defaultValue={isControlled ? undefined : defaultValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      error={captionIsError ? caption : undefined}
      hint={captionIsError ? undefined : caption}
    />
  )
})

DateInput.displayName = 'DateInput'
